import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { MarkerType } from '@apps/main/modules/maps/markerFormatters';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { MyPositionType } from '@libs/trip-planner-interfaces';
import type { PlaceId, Location } from '@libs/maps';

import type { UserLocation } from '../../models';
import { useGeolocationStore } from '../geolocationStore';
import { useMapStore } from '../mapStore/mapStore';
import type { MapStoreReturn } from '../mapStore/mapStore';

import {
  getDefaultLabel,
  mapUserLocationFromLocation,
} from './userLocationStoreLogic';

export const useUserLocationStore = defineStore('UserLocationStore', () => {
  const mapStore: MapStoreReturn = useMapStore();
  const geolocationStore = useGeolocationStore();
  const locationCache: Record<PlaceId, Location> = {};
  const { t } = i18n.global;

  // State
  const startLocation = ref<UserLocation>({
    name: getDefaultLabel(),
  });
  const endLocation = ref<UserLocation>({
    name: getDefaultLabel(),
  });
  const isUserPositionSame = computed(
    () =>
      startLocation.value.coordinates?.latitude ===
        endLocation.value.coordinates?.latitude &&
      startLocation.value.coordinates?.longitude ===
        endLocation.value.coordinates?.longitude
  );

  // Actions

  async function setUserStartLocation(
    placeId?: PlaceId,
    markerFormatter?: MarkerType
  ): Promise<void> {
    if (placeId) {
      const location =
        getLocationFromCache(placeId) ??
        (await mapStore.getPointGeolocationFromPlaceId(placeId));
      if (location) {
        setLocationCache(placeId, location);
        startLocation.value = mapUserLocationFromLocation(location);
      }
    } else {
      startLocation.value.name = getDefaultLabel();
      startLocation.value.country = undefined;

      if (geolocationStore.userGeolocation) {
        startLocation.value.coordinates = {
          latitude: geolocationStore.userGeolocation.latitude,
          longitude: geolocationStore.userGeolocation.longitude,
        };
      }
    }

    if (startLocation.value.coordinates) {
      const coordinates = startLocation.value.coordinates;
      mapStore.setPointLocation(MyPositionType.Start, {
        position: {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
        },
        formattedAddress:
          startLocation.value.name || t('planner.your_position'),
        markerFormatter,
      });
    }
  }

  async function setUserEndLocation(
    placeId?: PlaceId,
    markerFormatter?: MarkerType
  ): Promise<void> {
    if (placeId) {
      const location =
        getLocationFromCache(placeId) ??
        (await mapStore.getPointGeolocationFromPlaceId(placeId));
      if (location) {
        setLocationCache(placeId, location);
        endLocation.value = mapUserLocationFromLocation(location);
      }
    } else {
      endLocation.value.name = getDefaultLabel();
      endLocation.value.country = undefined;

      if (geolocationStore.userGeolocation) {
        endLocation.value.coordinates = {
          latitude: geolocationStore.userGeolocation.latitude,
          longitude: geolocationStore.userGeolocation.longitude,
        };
      }
    }

    if (endLocation.value.coordinates) {
      const coordinates = endLocation.value.coordinates;
      mapStore.setPointLocation(MyPositionType.End, {
        position: {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
        },
        formattedAddress: endLocation.value.name || t('planner.your_position'),
        markerFormatter,
      });
    }
  }

  function getLocationFromCache(placeId: PlaceId): Location | undefined {
    return locationCache[placeId];
  }

  function setLocationCache(placeId: PlaceId, location: Location): void {
    locationCache[placeId] = location;
  }

  return {
    // State
    startLocation,
    endLocation,
    isUserPositionSame,
    // Actions
    setUserStartLocation,
    setUserEndLocation,
  };
});
